import React, { useState, useRef } from 'react';
import { CrisisCommunicationService } from '../../services/crisisCommunicationService';

interface BulkImportPanelProps {
  onImport: (file: File) => void;
  isImporting: boolean;
  onCancel: () => void;
}

const BulkImportPanel: React.FC<BulkImportPanelProps> = ({ onImport, isImporting, onCancel }) => {
  const [dragActive, setDragActive] = useState(false);
  const [step, setStep] = useState<'download' | 'upload'>('download');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        onImport(file);
      } else {
        alert('Please select a CSV file');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onImport(e.target.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = async () => {
    try {
      const template = await CrisisCommunicationService.getCSVTemplate();
      const csvContent = atob(template.csv_content);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setStep('upload');
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const requiredFields = ['first_name', 'last_name', 'email'];
  const optionalFields = ['middle_name', 'phone', 'notes'];
  const sampleData =
    'John,Michael,Doe,john.doe@company.com,+1-555-0123,Emergency contact for IT department';

  return (
    <div className="relative">
      <div className="flex items-center justify-center mb-8">
        <div className="flex items-center space-x-4">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${
              step === 'download' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
            }`}
          >
            <span className="text-sm font-medium">1</span>
          </div>
          <div className={`w-16 h-1 ${step === 'upload' ? 'bg-green-600' : 'bg-gray-300'}`} />
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full ${
              step === 'upload' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
            }`}
          >
            <span className="text-sm font-medium">2</span>
          </div>
        </div>
      </div>

      {step === 'download' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Step 1: Download Template</h4>
            <p className="text-gray-600 mb-6">
              Download the template, fill it with your data, then upload it back
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-2">Required Fields:</h5>
            <div className="flex flex-wrap gap-2">
              {requiredFields.map((field) => (
                <span key={field} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                  {field}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-2">Optional Fields:</h5>
            <div className="flex flex-wrap gap-2">
              {optionalFields.map((field) => (
                <span key={field} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  {field}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-2">Sample Data:</h5>
            <code className="text-xs text-gray-600 bg-white p-2 rounded block overflow-x-auto">
              {sampleData}
            </code>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Download Template
            </button>
          </div>
        </div>
      )}

      {step === 'upload' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Step 2: Upload Filled Template</h4>
            <p className="text-gray-600 mb-6">Upload your completed CSV file to import all users at once</p>
          </div>

          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="space-y-4">
              <div className="mx-auto w-12 h-12 text-gray-400">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900">
                  Drop your CSV file here, or{' '}
                  <button
                    type="button"
                    onClick={handleClick}
                    className="text-blue-600 hover:text-blue-500 underline"
                  >
                    browse
                  </button>
                </p>
                <p className="text-sm text-gray-500">CSV files only</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          <div className="flex justify-center space-x-4">
            <button
              type="button"
              onClick={() => setStep('download')}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Back to Download
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isImporting && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl">
          <div className="text-center">
            <div className="mx-auto mb-4 w-8 h-8 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600" />
            <p className="text-gray-600">Importing users...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImportPanel;
